import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {toast} from 'react-toastify';
import {APIClient} from '../../utils/APIClient';
import {API_URL} from '../../settings/local_settings';
import {Spinner} from '../Spinner';
import {FaIcon} from '../FaIcon';
import {Switch} from '../buttons/Swtich';
import {buildVoicePreview} from './agencyVoicePreview';

const TONES = ['CERCANO', 'FORMAL', 'EJECUTIVO'];
const ALERT_HANDLINGS = ['NORMAL', 'SUAVIZAR', 'DERIVAR_A_AGENCIA'];
const MAX_TOPICS = 10;
const MAX_TOPIC_LENGTH = 120;
const MAX_GUIDELINES_LENGTH = 600;

/** El payload puede venir pelado (contrato) o envuelto en {status:'OK', data} (idiom del resto
 *  del Backend): se acepta cualquiera de los dos para no depender del detalle. */
function unwrap(data) {
    if (data && data.status === 'OK' && data.data) return data.data;
    return data;
}

/**
 * ¿Este usuario puede guardar?
 *
 * Lo resuelve el BACKEND y viaja en la misma respuesta (`can_edit`, GET y PUT de
 * `agent/agency-voice/`): es `user_type_is_admin()` del servidor. `LocalStorage` no sirve para esto
 * — `logged_user_type` guarda AGENCY_USER / CLIENT_USER / CRM_USER, que es otra dimensión.
 *
 * **Falla ABIERTO**: si la clave no viene (backend anterior al contrato — no deployan juntos), se
 * asume editable. Este gate es de UX, para no hacerle llenar un formulario a alguien que no lo puede
 * guardar; el dato lo protege el 403 del PUT, que sigue manejado. Fallar cerrado dejaría a un admin
 * mirando una pantalla de sólo lectura durante toda la ventana de skew.
 */
function resolveCanEdit(body) {
    return body.can_edit !== false;
}

/**
 * «Cómo le habla la IA a tus clientes» — la voz de la agencia (config a nivel AGENCIA).
 *
 * Gobierna el tono, los temas vedados y el tratamiento de urgencias del asistente que atiende a
 * los clientes en el portal; el switch avanzado la extiende a la IA del propio equipo. Cualquier
 * usuario de la agencia puede LEERLA; guardar es de administradores — el formulario se muestra en
 * sólo lectura con el motivo a la vista, y el 403 del backend sigue siendo la autoridad final.
 */
export function AgencyVoiceSettings() {
    const {t} = useTranslation();
    const [loading, setLoading] = useState(true);
    const [loadFailed, setLoadFailed] = useState(false);
    const [saving, setSaving] = useState(false);
    const [canEdit, setCanEdit] = useState(true);
    const [tone, setTone] = useState('');
    const [alertHandling, setAlertHandling] = useState('NORMAL');
    const [topics, setTopics] = useState([]);
    const [topicDraft, setTopicDraft] = useState('');
    const [guidelines, setGuidelines] = useState('');
    const [appliesToAgency, setAppliesToAgency] = useState(false);
    const [preview, setPreview] = useState('');
    // Lo último que el backend confirmó. Sirve para saber si el formulario está sucio, o sea si la
    // vista previa guardada todavía describe lo que la IA va a leer.
    const [saved, setSaved] = useState({tone: '', alertHandling: 'NORMAL', topics: [], guidelines: ''});

    const applyPayload = useCallback((body) => {
        const nextTone = body.tone || '';
        const nextAlert = body.alert_handling || 'NORMAL';
        const nextTopics = Array.isArray(body.avoid_topics) ? body.avoid_topics : [];
        const nextGuidelines = body.extra_guidelines || '';
        setTone(nextTone);
        setAlertHandling(nextAlert);
        setTopics(nextTopics);
        setGuidelines(nextGuidelines);
        setAppliesToAgency(Boolean(body.applies_to_agency_users));
        setPreview(body.preview || '');
        setCanEdit(resolveCanEdit(body));
        setSaved({
            tone: nextTone, alertHandling: nextAlert, topics: nextTopics, guidelines: nextGuidelines,
        });
    }, []);

    // Un solo request: el permiso viaja con la config (`can_edit`), así que el estado de sólo
    // lectura está desde el primer render — sin request extra y sin flash de formulario editable.
    const fetchVoice = useCallback(() => {
        setLoading(true);
        setLoadFailed(false);
        new APIClient(`${API_URL}agent/agency-voice/`).call('GET').then((data) => {
            setLoading(false);
            if (!data || data.status === 'ERROR' || data.status === 'NETWORK_ERROR') {
                setLoadFailed(true);
                return;
            }
            applyPayload(unwrap(data));
        });
    }, [applyPayload]);

    useEffect(() => {
        fetchVoice();
    }, [fetchVoice]);

    const addTopic = useCallback(() => {
        const clean = topicDraft.trim().slice(0, MAX_TOPIC_LENGTH);
        if (!clean) return;
        setTopics((prev) => {
            if (prev.length >= MAX_TOPICS) return prev;
            if (prev.some((topic) => topic.toLowerCase() === clean.toLowerCase())) return prev;
            return [...prev, clean];
        });
        setTopicDraft('');
    }, [topicDraft]);

    const removeTopic = useCallback((topic) => {
        setTopics((prev) => prev.filter((item) => item !== topic));
    }, []);

    const handleTopicKeyDown = useCallback((event) => {
        if (event.key === 'Enter') {
            event.preventDefault();
            addTopic();
        }
    }, [addTopic]);

    const handleSave = useCallback(() => {
        if (saving || !canEdit) return;
        setSaving(true);
        const payload = {
            tone,
            avoid_topics: topics,
            alert_handling: alertHandling,
            extra_guidelines: guidelines,
            applies_to_agency_users: appliesToAgency,
        };
        new APIClient(`${API_URL}agent/agency-voice/`).call('PUT', payload).then((data) => {
            setSaving(false);
            if (data && (data.http_status === 403 || data.error_code === 'FORBIDDEN')) {
                // `can_edit` es de UX; el backend es la autoridad. Si igual llegamos acá (permiso
                // cambiado con la sesión abierta, o backend viejo que no manda la clave y el gate
                // falló abierto), la pantalla pasa a sólo lectura con el motivo, en vez de dejar
                // un toast que se va solo.
                setCanEdit(false);
                toast.error(t('agency_voice.forbidden'));
                return;
            }
            if (!data || data.status === 'ERROR' || data.status === 'NETWORK_ERROR') {
                toast.error(t('agency_voice.save_error'));
                return;
            }
            // El PUT devuelve el mismo shape que el GET (preview incluida): la vista previa se
            // refresca con lo que el backend realmente guardó, no con lo que se mandó.
            applyPayload(unwrap(data));
            toast.success(t('agency_voice.saved_ok'));
        });
    }, [saving, canEdit, tone, topics, alertHandling, guidelines, appliesToAgency, applyPayload, t]);

    // El bloque que la IA leería con lo que hay AHORA en el formulario. Réplica del servicio del
    // backend (ver `agencyVoicePreview`); en cuanto se guarda, el panel vuelve al `preview`
    // autoritativo que devuelve el PUT.
    const draftPreview = useMemo(
        () => buildVoicePreview({tone, alertHandling, topics, guidelines}),
        [tone, alertHandling, topics, guidelines],
    );

    // `applies_to_agency_users` no entra: no cambia el contenido del bloque (la vista previa es la
    // del canal cliente), así que marcaría "sin guardar" un preview que sigue siendo exacto.
    const isDirty = useMemo(() => (
        tone !== saved.tone
        || alertHandling !== saved.alertHandling
        || guidelines !== saved.guidelines
        || topics.length !== saved.topics.length
        || topics.some((topic, index) => topic !== saved.topics[index])
    ), [tone, alertHandling, guidelines, topics, saved]);

    if (loading) {
        return (
            <div className="agency-voice box">
                <div className="box-loader"><Spinner color="positive"/></div>
            </div>
        );
    }

    if (loadFailed) {
        return (
            <div className="agency-voice box">
                <div className="av-error">
                    <FaIcon iconName="circle-exclamation"/>
                    <span>{t('agency_voice.load_error')}</span>
                    <button type="button" className="btn -alt -negative" onClick={fetchVoice}>
                        {t('agency_voice.retry')}
                    </button>
                </div>
            </div>
        );
    }

    const topicsFull = topics.length >= MAX_TOPICS;
    const readOnly = !canEdit;
    const shownPreview = isDirty ? draftPreview : preview;

    return (
        <div className={`agency-voice box ${readOnly ? 'is-readonly' : ''}`}>
            <header className="av-header">
                <h3 className="box-title">{t('agency_voice.title')}</h3>
                <p className="av-subtitle">{t('agency_voice.subtitle')}</p>
            </header>

            {/* El motivo, desde el arranque y en pantalla: un toast después de completar el
                formulario llega tarde y se va solo. */}
            {readOnly && (
                <p className="av-readonly-notice">
                    <FaIcon iconName="lock"/>
                    <span>{t('agency_voice.read_only_notice')}</span>
                </p>
            )}

            <div className="av-body">
                <div className="av-form">
                    <div className="form-row">
                        <label>{t('agency_voice.tone_label')}</label>
                        <select
                            className="form-control"
                            value={tone}
                            disabled={readOnly}
                            onChange={(e) => setTone(e.target.value)}
                        >
                            <option value="">{t('agency_voice.tone_none')}</option>
                            {TONES.map((option) => (
                                <option key={option} value={option}>
                                    {t(`agency_voice.tone_options.${option}`)}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div className="form-row">
                        <label>{t('agency_voice.alerts_label')}</label>
                        <div className="av-radio-group" role="radiogroup" aria-label={t('agency_voice.alerts_label')}>
                            {ALERT_HANDLINGS.map((option) => (
                                <label key={option} className={`av-radio-option ${alertHandling === option ? 'is-on' : ''}`}>
                                    <input
                                        type="radio"
                                        name="agency-voice-alerts"
                                        checked={alertHandling === option}
                                        disabled={readOnly}
                                        onChange={() => setAlertHandling(option)}
                                    />
                                    <span className="av-radio-texts">
                                        <span className="av-radio-title">{t(`agency_voice.alert_options.${option}`)}</span>
                                        <span className="av-radio-desc">{t(`agency_voice.alert_options.${option}_desc`)}</span>
                                    </span>
                                </label>
                            ))}
                        </div>
                    </div>

                    <div className="form-row">
                        <label>{t('agency_voice.topics_label')}</label>
                        <p className="av-hint">{t('agency_voice.topics_hint')}</p>
                        {topics.length > 0 && (
                            <div className="av-chips">
                                {topics.map((topic) => (
                                    <span key={topic} className="av-chip">
                                        {topic}
                                        {!readOnly && (
                                            <button
                                                type="button"
                                                className="av-chip-remove"
                                                aria-label={t('agency_voice.topics_remove', {topic, interpolation: {escapeValue: false}})}
                                                onClick={() => removeTopic(topic)}
                                            >
                                                <FaIcon iconName="times"/>
                                            </button>
                                        )}
                                    </span>
                                ))}
                            </div>
                        )}
                        {!readOnly && (
                            <>
                                <div className="av-chip-add">
                                    <input
                                        className="form-control"
                                        type="text"
                                        value={topicDraft}
                                        maxLength={MAX_TOPIC_LENGTH}
                                        disabled={topicsFull}
                                        placeholder={t(topicsFull ? 'agency_voice.topics_full' : 'agency_voice.topics_placeholder')}
                                        onChange={(e) => setTopicDraft(e.target.value)}
                                        onKeyDown={handleTopicKeyDown}
                                    />
                                    <button
                                        type="button"
                                        className="btn -alt -negative"
                                        onClick={addTopic}
                                        disabled={topicsFull || !topicDraft.trim()}
                                    >
                                        <FaIcon iconName="plus"/> {t('agency_voice.topics_add')}
                                    </button>
                                </div>
                                <p className="av-counter">{t('agency_voice.topics_counter', {count: topics.length, max: MAX_TOPICS})}</p>
                            </>
                        )}
                    </div>

                    <div className="form-row">
                        <label>{t('agency_voice.guidelines_label')}</label>
                        <textarea
                            className="form-control av-textarea"
                            rows={4}
                            maxLength={MAX_GUIDELINES_LENGTH}
                            value={guidelines}
                            disabled={readOnly}
                            placeholder={t('agency_voice.guidelines_placeholder')}
                            onChange={(e) => setGuidelines(e.target.value)}
                        />
                        {!readOnly && <p className="av-counter">{guidelines.length}/{MAX_GUIDELINES_LENGTH}</p>}
                    </div>

                    <div className="form-row switch-input av-switch-row">
                        <div className="label-with-explanation">
                            <label>{t('agency_voice.applies_label')}</label>
                            <p>{t('agency_voice.applies_hint')}</p>
                        </div>
                        <Switch
                            name="agency-voice-applies"
                            checked={appliesToAgency}
                            disabled={readOnly}
                            onChange={(e) => setAppliesToAgency(e.target.checked)}
                            style={{margin: 0}}
                        />
                    </div>

                    {!readOnly && (
                        <div className="submit-row av-submit">
                            <button type="button" className="btn -alt" onClick={handleSave} disabled={saving}>
                                {t(saving ? 'agency_voice.saving' : 'agency_voice.save')}
                            </button>
                        </div>
                    )}
                </div>

                <aside className={`av-preview ${isDirty ? 'is-draft' : ''}`}>
                    <h4 className="av-preview-title">
                        <FaIcon iconName="eye"/> {t('agency_voice.preview_title')}
                        {isDirty && (
                            <span className="av-preview-badge">{t('agency_voice.preview_unsaved_badge')}</span>
                        )}
                    </h4>
                    <p className="av-preview-hint">
                        {t(isDirty ? 'agency_voice.preview_unsaved_hint' : 'agency_voice.preview_hint')}
                    </p>
                    {shownPreview
                        ? <pre className="av-preview-body">{shownPreview}</pre>
                        : <p className="av-preview-empty">
                            {t(isDirty ? 'agency_voice.preview_draft_empty' : 'agency_voice.preview_empty')}
                        </p>}
                </aside>
            </div>
        </div>
    );
}

export default AgencyVoiceSettings;
